import type {
    GlobalsQuery as GlobalsQueryType,
    SeoFieldFragment,
    FairUsePolicyFragment,
    MembershipFragment,
    MainNavigationFragment,
    FooterFragment,
    WaitingListFragment
} from '~/types/craft-schema';
import GlobalsQuery from '~/graphql/queries/Globals.graphql';
import { useEventsStore } from '~/stores/events';
import { useGlobalStore } from '~/stores/global';
import { useNavigationStore } from '~/stores/navigation';

function logMissingGlobalSet(globalSetName: string) {
    if (import.meta.server) {
        // eslint-disable-next-line no-console
        console.warn(`Coulnd't retrieve (${globalSetName})`);
    }
}

export default defineNuxtPlugin(async() => {
    const globalStore = useGlobalStore();

    if (!globalStore.fetched) {
        /**
         * Craft data
         */
        const { data: craftData, error: craftError } = await useAsyncQuery<GlobalsQueryType>({
            query: GlobalsQuery
        });

        if (craftError.value) {
            if (process.env.APP_ENV === 'development') {
                console.error(craftError.value);
            }

            console.error('Coulnd\'t retrieve craft global data in plugins/globalData.ts');
        }

        if (!craftData.value) {
            return;
        }

        const navigationStore = useNavigationStore();
        const eventsStore = useEventsStore();

        const {
            seo,
            membership,
            mainNavigation,
            footer,
            fairUsePolicy,
            waitingList
        } = craftData.value;

        globalStore.fetched = true;

        if (!seo?.seo) {
            logMissingGlobalSet('Ether_SeoData');
        } else {
            globalStore.seo = seo.seo as SeoFieldFragment;
        }

        if (!membership) {
            logMissingGlobalSet('Membership_GlobalSet');
        } else {
            globalStore.setMembershipData(membership as MembershipFragment);
        }

        if (!mainNavigation) {
            logMissingGlobalSet('MainNavigation_GlobalSet');
        } else {
            navigationStore.setMainNavigation(mainNavigation as MainNavigationFragment);
        }

        if (!footer) {
            logMissingGlobalSet('Footer_GlobalSet');
        } else {
            navigationStore.setFooter(footer as FooterFragment);
        }

        if (!fairUsePolicy) {
            logMissingGlobalSet('FairUsePolicy_GlobalSet');
        } else {
            eventsStore.setFairUsePolicy(fairUsePolicy as FairUsePolicyFragment);
        }

        if (!waitingList) {
            logMissingGlobalSet('WaitingList_GlobalSet');
        } else {
            eventsStore.setWaitingListInfo(waitingList as WaitingListFragment);
        }

        /**
         * Laravel data
         */
        const { data: laravelData, error } = await useFetchLaravel<{ max_reservations: number }>('/v2.0/global-data');

        if (error.value) {
            console.error(error.value);
            throw new Error('Coulnd\'t retrieve laravel global data in plugins/globalData.ts');
        }

        if (laravelData.value) {
            globalStore.setMaxReservations(laravelData.value.max_reservations);
        }
    }
});
